import {
  AlertCircle,
  ArrowDownRight,
  CheckCircle2,
  Package,
} from "lucide-react";
import { useState } from "react";

import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import { DatePicker } from "@/components/shared/date-picker";
import { SearchableSelect } from "@/components/shared/searchable-select";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { NumberInput } from "@/components/ui/number-input";
import { Textarea } from "@/components/ui/textarea";
import { useNextNumberPreview } from "@/hooks/use-number-sequences";

import { RepackingOutputField } from "./repacking-output-field";
import { useRepackingForm } from "./use-repacking-form";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export function RepackingDialog({ open, onOpenChange, onSuccess }: Props) {
  const { preview: sequencePreview } = useNextNumberPreview("stock_repack");
  const form = useRepackingForm({ open, onOpenChange, onSuccess });
  const { selectedItem, isSubmitting, isLoadingStocks } = form;
  const [confirmOpen, setConfirmOpen] = useState(false);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between gap-2 pr-6">
            <div className="flex items-center gap-2">
              <Package className="h-5 w-5 text-primary" />
              <DialogTitle className="text-base font-semibold">
                Buka Kemasan & Repacking Bar
              </DialogTitle>
            </div>
            {sequencePreview && (
              <Badge variant="outline" className="font-mono text-xs">
                {sequencePreview}
              </Badge>
            )}
          </div>
          <DialogDescription className="text-xs">
            Buka barang beli dari gudang menjadi bahan resep di bar (contoh: 2
            kotak Susu Diamond → 2.000 mL Susu UHT), atau pindahkan barang
            langsung pakai ke bar.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2 text-xs">
          {/* Tanggal & Bahan Baku Asal */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div>
              <Label htmlFor="date" className="text-xs font-medium">
                Tanggal
              </Label>
              <DatePicker
                value={form.date}
                onChange={form.setDate}
                className="mt-1"
              />
            </div>

            <div className="sm:col-span-2">
              <Label className="text-xs font-medium">
                Barang dari Gudang <span className="text-destructive">*</span>
              </Label>
              <SearchableSelect
                value={form.selectedRawMaterialId}
                onChange={form.setSelectedRawMaterialId}
                disabled={isLoadingStocks}
                placeholder={
                  isLoadingStocks
                    ? "Memuat bahan baku..."
                    : "Cari barang beli / langsung pakai..."
                }
                options={form.rawMaterials.map((rm) => ({
                  value: rm.itemId,
                  label: rm.name,
                  hint: `Segel: ${rm.onHandQty.toLocaleString("id-ID")} · Bar: ${rm.repackingQty.toLocaleString("id-ID")} ${rm.uomName}`,
                }))}
                className="mt-1 h-9 text-xs"
              />
            </div>
          </div>

          {/* Selected Item Status Banner */}
          {selectedItem && (
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-lg border bg-muted/40 p-2.5">
              <div>
                <p className="font-semibold text-foreground text-xs">
                  {selectedItem.name}
                </p>
                {form.opensInto && (
                  <p className="text-[11px] text-muted-foreground">
                    Dibuka jadi {form.opensInto.name}
                  </p>
                )}
              </div>
              <div className="flex gap-4 sm:gap-6 justify-between sm:justify-end text-left sm:text-right">
                <div>
                  <span className="text-[11px] text-muted-foreground block">
                    Di Gudang
                  </span>
                  <span className="font-bold text-sm text-foreground">
                    {selectedItem.onHandQty.toLocaleString("id-ID")}{" "}
                    {selectedItem.uomName}
                  </span>
                </div>
                <div>
                  <span className="text-[11px] text-muted-foreground block">
                    Di Bar{form.opensInto ? ` (${form.opensInto.name})` : ""}
                  </span>
                  <span className="font-bold text-sm text-foreground">
                    {(form.outputStock?.repackingQty ?? 0).toLocaleString(
                      "id-ID",
                    )}{" "}
                    {form.outputUom}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Input Qty */}
          <div className="rounded-lg border p-3">
            <div className="flex items-center justify-between">
              <Label
                htmlFor="input-qty"
                className="text-xs font-semibold flex items-center gap-1.5"
              >
                <ArrowDownRight className="h-4 w-4 text-amber-500" />
                Jumlah Dibuka (Segel → Bar){" "}
                <span className="text-destructive">*</span>
              </Label>
              {selectedItem && (
                <span className="text-[11px] text-muted-foreground">
                  Satuan: <strong>{selectedItem.uomName}</strong>
                </span>
              )}
            </div>

            <div className="mt-2 flex items-center gap-2">
              <NumberInput
                id="input-qty"
                decimals={4}
                placeholder={
                  selectedItem ? `Contoh: 2 (${selectedItem.uomName})` : "0"
                }
                value={form.inputQtyStr ? Number(form.inputQtyStr) : undefined}
                onChange={(n) => form.setInputQtyStr(n ? String(n) : "")}
                className="h-10 text-xs sm:h-9"
              />
              <span className="min-w-14 text-center font-medium text-xs text-muted-foreground">
                {selectedItem?.uomName || "unit"}
              </span>
            </div>

            {form.isInputGreaterThanOnHand && (
              <p className="mt-1.5 text-[11px] text-amber-600 dark:text-amber-400 flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />
                Peringatan: Jumlah dibuka melebihi saldo on-hand segel saat ini
                ({selectedItem?.onHandQty} {selectedItem?.uomName}).
              </p>
            )}
          </div>

          <RepackingOutputField form={form} />

          {/* General Note */}
          <div className="space-y-1">
            <Label htmlFor="note" className="text-xs font-medium">
              Catatan Tambahan (Opsional)
            </Label>
            <Textarea
              id="note"
              placeholder="Contoh: Buka pack baru batch sangrai 12 Okt..."
              value={form.generalNote}
              onChange={(e) => form.setGeneralNote(e.target.value)}
              rows={2}
              className="resize-none text-xs"
            />
          </div>
        </div>

        <DialogFooter className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
            className="min-h-[40px] sm:min-h-9"
          >
            Batal
          </Button>

          <Button
            type="button"
            size="sm"
            disabled={!form.canSubmit}
            onClick={() => setConfirmOpen(true)}
            className="gap-1.5 min-h-[40px] sm:min-h-9"
          >
            <CheckCircle2 className="h-4 w-4" />
            {isSubmitting ? "Memproses..." : "Repack Sekarang"}
          </Button>
        </DialogFooter>
      </DialogContent>
      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="Repack sekarang?"
        description={`${form.inputQty} ${selectedItem?.uomName ?? ""} ${
          selectedItem?.name ?? "barang"
        } dibuka dan dipindahkan dari gudang ke bar. Stok langsung berubah dan tercatat di Kartu Stok.`}
        confirmLabel="Ya, Repack"
        variant="default"
        isConfirming={isSubmitting}
        onConfirm={async () => {
          await form.submit(true);
          setConfirmOpen(false);
        }}
      />
    </Dialog>
  );
}
